import { z } from 'zod';
import { UserSchema } from '../../entities/user/user.schema';
/** Why a difference is shown but cannot be selected. */ export const DataSyncBlockedReasonSchema = z.enum([
    'missingDepartment',
    'missingRole',
    'missingOwner',
    'nameTaken',
    'emailNotChangeable',
    'emailTaken',
    'externalPerson',
    'actingAdmin',
    'lastOrgManager',
    'inUse',
    /** An Excel run's change to a person, department or team Personio manages. */ 'personioLed'
]);
/**
 * The kinds of unit the source names and the sync renames, leads and deletes:
 * a department, a team, a Funktion (role-kind OrgFunction) or a system,
 * whose lead is the Systemverantwortlicher.
 */ export const DataSyncUnitTypeSchema = z.enum([
    'department',
    'team',
    'role',
    'system'
]);
export const DataSyncRefSchema = z.object({
    id: z.string(),
    name: z.string()
});
/** What still points at a leaver: deleting reassigns none of it. */ export const DataSyncLeaverHoldsSchema = z.object({
    departmentsManaged: z.array(DataSyncRefSchema),
    functionsLed: z.array(DataSyncRefSchema),
    processesOwned: z.array(DataSyncRefSchema),
    processesAccountable: z.array(DataSyncRefSchema),
    systemsOwned: z.array(DataSyncRefSchema),
    openAssignmentCount: z.number()
});
/** What still uses a unit the source no longer lists, counted per kind of referrer. */ export const DataSyncUnitUsageSchema = z.object({
    people: z.number().int().nonnegative(),
    subDepartments: z.number().int().nonnegative(),
    orgFunctions: z.number().int().nonnegative(),
    processes: z.number().int().nonnegative(),
    processSteps: z.number().int().nonnegative(),
    suggestions: z.number().int().nonnegative()
});
/**
 * Every create gets its Bliks id when the run enters review, and the
 * differences depending on it name that id, so no target is left open.
 */ const DifferenceBaseSchema = z.object({
    id: z.string(),
    /** Differences that must be selected for this one to apply. */ dependsOn: z.array(z.string()),
    blockedReason: DataSyncBlockedReasonSchema.nullable()
});
const DepartmentCreateSchema = DifferenceBaseSchema.extend({
    kind: z.literal('departmentCreate'),
    /** The id the apply creates the department with. */ departmentId: z.string(),
    key: z.string(),
    name: z.string(),
    abbreviation: z.string(),
    parentKey: z.string().nullable(),
    /** null without a parent. */ parentDepartmentId: z.string().nullable(),
    parentName: z.string().nullable()
});
const DepartmentMoveSchema = DifferenceBaseSchema.extend({
    kind: z.literal('departmentMove'),
    departmentId: z.string(),
    departmentName: z.string(),
    beforeParentId: z.string().nullable(),
    beforeParentName: z.string().nullable(),
    /** The after-parent fields are null for a move to the top level. */ afterParentKey: z.string().nullable(),
    afterParentDepartmentId: z.string().nullable(),
    /** From the snapshot: the new parent may be created in the same run. */ afterParentName: z.string().nullable()
});
const TeamCreateSchema = DifferenceBaseSchema.extend({
    kind: z.literal('teamCreate'),
    /** The id the apply creates the team with. */ orgFunctionId: z.string(),
    key: z.string(),
    name: z.string()
});
const RoleCreateSchema = DifferenceBaseSchema.extend({
    kind: z.literal('roleCreate'),
    /** The id the apply creates the Funktion with. */ orgFunctionId: z.string(),
    key: z.string(),
    name: z.string()
});
const PersonValueSchema = z.object({
    userId: z.string(),
    displayName: z.string()
});
/** A system needs an owner: without one the create is blocked `missingOwner`. */ const SystemCreateSchema = DifferenceBaseSchema.extend({
    kind: z.literal('systemCreate'),
    /** The id the apply creates the system with. */ systemId: z.string(),
    key: z.string(),
    name: z.string(),
    owner: PersonValueSchema.nullable()
});
/**
 * A unit as the source names it. `id` is the paired Bliks
 * entity, or the one this run creates (the difference then depends on that
 * create).
 */ const SourcedRefSchema = z.object({
    key: z.string(),
    id: z.string(),
    name: z.string()
});
const SourcedUnitSchema = SourcedRefSchema.extend({
    type: DataSyncUnitTypeSchema
});
/** `unit.name` is the source name the rename writes; `before` the Bliks name. */ const UnitRenameSchema = DifferenceBaseSchema.extend({
    kind: z.literal('unitRename'),
    unit: SourcedUnitSchema,
    before: z.string()
});
const PersonCreateSchema = DifferenceBaseSchema.extend({
    kind: z.literal('personCreate'),
    /** The id the apply creates the person with. */ userId: z.string(),
    sourceId: z.string().nullable(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    /** The department fields are null without a department, the team fields without a team. */ departmentKey: z.string().nullable(),
    departmentId: z.string().nullable(),
    departmentName: z.string().nullable(),
    /** The Stelle's title. */ role: z.string().nullable(),
    /** The Stelle's Funktion, paired or created by this run; null to find or create it by title. */ roleId: z.string().nullable(),
    teamKey: z.string().nullable(),
    teamId: z.string().nullable(),
    teamName: z.string().nullable(),
    /** The Standort in the spelling Bliks already uses; missing on runs reviewed before it existed. */ location: z.string().nullable().optional()
});
const PersonNameValueSchema = UserSchema.pick({
    firstName: true,
    lastName: true
});
const PersonEmailValueSchema = UserSchema.pick({
    email: true
});
const PersonLocationValueSchema = UserSchema.pick({
    location: true
}).required();
/**
 * A Stelle by key (Excel): `id` is the Funktion the apply writes, paired or
 * created by this run. By title (Personio): `id` is null and the apply finds
 * or creates the Funktion by title.
 */ const PersonRoleValueSchema = z.object({
    id: z.string().nullable(),
    name: z.string()
});
const PersonUpdateBaseSchema = DifferenceBaseSchema.extend({
    kind: z.literal('personUpdate'),
    userId: z.string(),
    displayName: z.string()
});
/**
 * `before` is the Bliks value at review time, `after` the source value. A
 * missing source value never proposes clearing, so `after` is never null.
 */ const PersonUpdateSchema = z.discriminatedUnion('field', [
    PersonUpdateBaseSchema.extend({
        field: z.literal('name'),
        before: PersonNameValueSchema,
        after: PersonNameValueSchema
    }),
    PersonUpdateBaseSchema.extend({
        field: z.literal('email'),
        before: PersonEmailValueSchema,
        after: PersonEmailValueSchema
    }),
    PersonUpdateBaseSchema.extend({
        field: z.literal('department'),
        before: DataSyncRefSchema.nullable(),
        after: SourcedRefSchema
    }),
    PersonUpdateBaseSchema.extend({
        field: z.literal('role'),
        before: DataSyncRefSchema.nullable(),
        after: PersonRoleValueSchema
    }),
    PersonUpdateBaseSchema.extend({
        field: z.literal('team'),
        before: DataSyncRefSchema.nullable(),
        after: SourcedRefSchema
    }),
    PersonUpdateBaseSchema.extend({
        field: z.literal('location'),
        before: PersonLocationValueSchema.nullable(),
        after: PersonLocationValueSchema
    })
]);
/**
 * A department's manager, a team's or Funktion's lead, or a system's owner.
 * `after` is a person the source lists, one this run creates, or a live
 * Bliks person the source names by id.
 */ const UnitLeadSchema = DifferenceBaseSchema.extend({
    kind: z.literal('unitLead'),
    unit: SourcedUnitSchema,
    before: PersonValueSchema.nullable(),
    after: PersonValueSchema
});
const LeaverSchema = DifferenceBaseSchema.extend({
    kind: z.literal('leaver'),
    userId: z.string(),
    displayName: z.string(),
    departmentName: z.string().nullable(),
    role: z.string().nullable(),
    /** inactive in the source, or absent from a complete read */ reason: z.enum([
        'inactive',
        'absent'
    ]),
    holds: DataSyncLeaverHoldsSchema
});
/**
 * A source-managed unit the source no longer lists. `usage` counts every
 * use at review time, including what other differences of the run remove.
 */ const UnitDeleteSchema = DifferenceBaseSchema.extend({
    kind: z.literal('unitDelete'),
    unit: DataSyncRefSchema.extend({
        type: DataSyncUnitTypeSchema
    }),
    usage: DataSyncUnitUsageSchema
});
/** One kind per difference; narrow with `Extract<DataSyncDifference, { kind: … }>`. */ export const DataSyncDifferenceSchema = z.discriminatedUnion('kind', [
    DepartmentCreateSchema,
    DepartmentMoveSchema,
    TeamCreateSchema,
    RoleCreateSchema,
    SystemCreateSchema,
    UnitRenameSchema,
    PersonCreateSchema,
    PersonUpdateSchema,
    UnitLeadSchema,
    LeaverSchema,
    UnitDeleteSchema
]);
/** Whether a difference can be selected: not blocked, and every difference it depends on selected. */ export function isSelectableDifference(difference, selected) {
    return difference.blockedReason === null && difference.dependsOn.every((id)=>selected.has(id));
}
/** Drops every selected difference whose dependency is not selected, transitively. */ export function withoutBrokenDependents(differences, selected) {
    const kept = new Set(selected);
    let dropped = true;
    while(dropped){
        dropped = false;
        for (const difference of differences){
            if (kept.has(difference.id) && !isSelectableDifference(difference, kept)) {
                kept.delete(difference.id);
                dropped = true;
            }
        }
    }
    return kept;
}
/** The differences a review starts with selected; an empty set leaves nothing to decide. */ export function selectableDifferenceIds(differences) {
    return withoutBrokenDependents(differences, new Set(differences.map(({ id })=>id)));
}

//# sourceMappingURL=data-sync-difference.schema.js.map